import { useState, useEffect, useCallback } from 'react';
import type { ScheduleData, Hymn, Week, Song } from '../types';
import { fetchScheduleData, fetchHymns, saveSchedule } from '../services/api';

export function useScheduleData() {
  const [data, setData] = useState<ScheduleData | null>(null);
  const [hymns, setHymns] = useState<Hymn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [scheduleData, hymnsData] = await Promise.all([
        fetchScheduleData(),
        fetchHymns(),
      ]);
      setData(scheduleData);
      setHymns(hymnsData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const updateWeek = useCallback((weekIndex: number, field: keyof Week, value: string) => {
    setData((prevData) => {
      if (!prevData) return prevData;
      const newWeeks = [...prevData.weeks];
      newWeeks[weekIndex] = { ...newWeeks[weekIndex], [field]: value };
      return { ...prevData, weeks: newWeeks };
    });
  }, []);

  const updateSong = useCallback(
    (weekIndex: number, songIndex: number, field: keyof Song, value: string | boolean) => {
      setData((prevData) => {
        if (!prevData) return prevData;
        const newWeeks = [...prevData.weeks];
        const newSongs = [...newWeeks[weekIndex].songs];
        newSongs[songIndex] = { ...newSongs[songIndex], [field]: value };
        newWeeks[weekIndex] = { ...newWeeks[weekIndex], songs: newSongs };
        return { ...prevData, weeks: newWeeks };
      });
    },
    []
  );

  const addWeek = useCallback(() => {
    setData((prevData) => {
      if (!prevData) return prevData;
      const today = new Date();
      const dayOfWeek = today.getDay();
      const daysUntilSunday = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
      const nextSunday = new Date(today);
      nextSunday.setDate(today.getDate() + daysUntilSunday);

      const year = nextSunday.getFullYear();
      const month = String(nextSunday.getMonth() + 1).padStart(2, '0');
      const day = String(nextSunday.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;

      const newWeek: Week = {
        id: Date.now().toString(),
        date: dateStr,
        pianist: '',
        conductor: '',
        notes: '',
        songs: [
          { id: '1', hymnId: '', isSpecial: false, performerName: '', songName: '', performerPhone: '' },
          { id: '2', hymnId: '', isSpecial: false, performerName: '', songName: '', performerPhone: '' },
          { id: '3', hymnId: '', isSpecial: false, performerName: '', songName: '', performerPhone: '' },
        ],
      };

      return { ...prevData, weeks: [newWeek, ...prevData.weeks] };
    });
  }, []);

  const deleteWeek = useCallback((weekIndex: number) => {
    setData((prevData) => {
      if (!prevData) return prevData;
      const newWeeks = prevData.weeks.filter((_, i) => i !== weekIndex);
      return { ...prevData, weeks: newWeeks };
    });
  }, []);

  const addSong = useCallback((weekIndex: number) => {
    setData((prevData) => {
      if (!prevData) return prevData;
      if (prevData.weeks[weekIndex].songs.length >= 5) return prevData;

      const newWeeks = [...prevData.weeks];
      const newSongs = [
        ...newWeeks[weekIndex].songs,
        {
          id: Date.now().toString(),
          hymnId: '',
          isSpecial: false,
          performerName: '',
          songName: '',
          performerPhone: '',
        },
      ];
      newWeeks[weekIndex] = { ...newWeeks[weekIndex], songs: newSongs };
      return { ...prevData, weeks: newWeeks };
    });
  }, []);

  const removeSong = useCallback((weekIndex: number, songIndex: number) => {
    setData((prevData) => {
      if (!prevData) return prevData;
      if (prevData.weeks[weekIndex].songs.length <= 3) return prevData;

      const newWeeks = [...prevData.weeks];
      const newSongs = newWeeks[weekIndex].songs.filter((_, i) => i !== songIndex);
      newWeeks[weekIndex] = { ...newWeeks[weekIndex], songs: newSongs };
      return { ...prevData, weeks: newWeeks };
    });
  }, []);

  const save = useCallback(async () => {
    if (!data) return false;
    try {
      const result = await saveSchedule(data);
      return result.success;
    } catch {
      return false;
    }
  }, [data]);

  const getHymnDisplayText = useCallback(
    (hymnId: string) => {
      if (!hymnId) return '';
      const hymn = hymns.find((h) => String(h.number) === String(hymnId));
      return hymn ? `${hymn.number} - ${hymn.title}` : '';
    },
    [hymns]
  );

  const getPianistName = useCallback(
    (pianistId: string) => {
      if (!data) return 'No pianist assigned';
      const pianist = data.pianists.find((p) => p.id === pianistId);
      return pianist ? pianist.name : 'No pianist assigned';
    },
    [data]
  );

  const getConductorName = useCallback(
    (conductorId: string) => {
      if (!data) return '';
      const conductor = data.conductors.find((c) => c.id === conductorId);
      return conductor ? conductor.name : '';
    },
    [data]
  );

  return {
    data,
    hymns,
    loading,
    error,
    reload: loadData,
    updateWeek,
    updateSong,
    addWeek,
    deleteWeek,
    addSong,
    removeSong,
    save,
    getHymnDisplayText,
    getPianistName,
    getConductorName,
  };
}
